import { createAdminRepository } from "data/admin/repositoryFactory";
import { normalizeAd } from "data/admin/domain";
import { explainFirebaseError, isFirebaseConfigured, isPermissionDeniedError } from "data/firebase";
import {
  getAdFromFirestore,
  listAdsFromFirestore,
  restoreAdInFirestore,
  saveAdToFirestore,
  seedAdsToFirestore,
  seedAdsToFirestoreIfEmpty,
  softDeleteAdInFirestore,
  uploadAdImage,
} from "data/firebase/adsApi";

const localAds = createAdminRepository({
  collectionName: "ads",
  idPrefix: "ad",
  getId: (item) => item.id,
  normalize: normalizeAd,
});

let firebaseAdsUnavailable = false;
let lastFirebaseWarning = "";

function sortAds(items) {
  return [...items].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.title || "").localeCompare(String(b.title || ""))
  );
}

function matchesPlacement(ad, placement) {
  if (!placement) return true;
  return ad.placement === placement || ad.placement === "all";
}

function markFirebaseUnavailable(error) {
  firebaseAdsUnavailable = true;
  lastFirebaseWarning = explainFirebaseError(error);
  if (typeof console !== "undefined") {
    console.warn("[adsRepo]", lastFirebaseWarning, error);
  }
}

function shouldUseFirebase() {
  return isFirebaseConfigured && !firebaseAdsUnavailable;
}

async function withFirebaseOrLocal(firebaseFn, localFn) {
  if (!shouldUseFirebase()) {
    return localFn();
  }
  try {
    return await firebaseFn();
  } catch (error) {
    if (isPermissionDeniedError(error) || error?.code === "auth/operation-not-allowed") {
      markFirebaseUnavailable(error);
      return localFn();
    }
    throw error;
  }
}

export const adsRepo = {
  usesFirebase: () => shouldUseFirebase(),

  getFirebaseWarning() {
    return lastFirebaseWarning;
  },

  /** Kurallar düzeltildikten sonra tekrar Firebase dene. */
  resetFirebaseAvailability() {
    firebaseAdsUnavailable = false;
    lastFirebaseWarning = "";
  },

  async list(params = {}) {
    return withFirebaseOrLocal(
      async () => {
        const items = await listAdsFromFirestore({ includeDeleted: Boolean(params.includeDeleted) });
        const filtered = typeof params.filter === "function" ? items.filter(params.filter) : items;
        return sortAds(filtered);
      },
      () => sortAds(localAds.list(params))
    );
  },

  async listByPlacement(placement, params = {}) {
    const items = await this.list({
      ...params,
      includeDeleted: false,
    });
    return items.filter((item) => item.isActive !== false && matchesPlacement(item, placement));
  },

  async getById(id) {
    return withFirebaseOrLocal(
      () => getAdFromFirestore(id),
      () => localAds.getById(id, { includeDeleted: true })
    );
  },

  async create(input) {
    return withFirebaseOrLocal(
      () => saveAdToFirestore({ ...input, deletedAt: null, isActive: input.isActive !== false }),
      () => localAds.create(input)
    );
  },

  async update(id, patch) {
    return withFirebaseOrLocal(
      async () => {
        const current = (await getAdFromFirestore(id)) || { id };
        return saveAdToFirestore({ ...current, ...patch, id });
      },
      () => localAds.update(id, patch)
    );
  },

  async remove(id) {
    return withFirebaseOrLocal(
      async () => {
        await softDeleteAdInFirestore(id);
        return true;
      },
      () => localAds.remove(id)
    );
  },

  async restore(id) {
    return withFirebaseOrLocal(
      async () => {
        await restoreAdInFirestore(id);
        return true;
      },
      () => localAds.restore(id)
    );
  },

  async uploadImage(file, adId) {
    if (!shouldUseFirebase()) {
      throw new Error(
        lastFirebaseWarning ||
          "Görsel yükleme için Firebase gerekir. Kuralları düzeltene kadar görsel URL alanını kullanın."
      );
    }
    try {
      return await uploadAdImage(file, adId);
    } catch (error) {
      if (isPermissionDeniedError(error) || error?.code === "auth/operation-not-allowed") {
        markFirebaseUnavailable(error);
      }
      throw new Error(explainFirebaseError(error));
    }
  },

  async seedDefaults({ force = false } = {}) {
    return withFirebaseOrLocal(
      () => (force ? seedAdsToFirestore({ force: true }) : seedAdsToFirestoreIfEmpty()),
      async () => {
        const existing = localAds.list({ includeDeleted: true });
        if (existing.length && !force) return { seeded: false, count: existing.length };
        const { seedAds } = await import("data/seedAds");
        if (force) {
          // local: recreate from seed
          seedAds.forEach((ad) => {
            try {
              localAds.create(ad);
            } catch {
              localAds.update(ad.id, ad);
            }
          });
          return { seeded: true, count: seedAds.length, forced: true };
        }
        seedAds.forEach((ad) => localAds.create(ad));
        return { seeded: true, count: seedAds.length };
      }
    );
  },
};
